import type { Balance } from '@polkadot/types/interfaces'
import type { AnyEventObject } from 'xstate'
import type { INftSaleMachine, NftSaleContext } from '../nftSaleMachine'

import { useEffect } from 'react'
import { createMachine } from 'xstate'
import { useAtomValue, useSetAtom } from 'jotai'
import { queryClientAtom } from 'jotai/query'
import { atomWithMachine } from 'jotai/xstate'
import Decimal from 'decimal.js'
import { isRight } from 'fp-ts/lib/Either'
import * as R from 'ramda'

import { LevelEnum, RaceEnum, CareerEnum, LEVEL_COLOR_MAP, RACE_CHAIN_MAP } from '@/types/nftAttribute'
import { pwapiEndpointPrefixAtom, PWAPI_ENDPOINT_PREFIX } from '@/features/spirit/atoms/hosts'
import { subqlEndpointAtom } from '@/atoms/subqlEndpointAtom'
import {
  getOriginOfShellCollectionForAccountAndId,
  // extrinstic
  preOrderOriginOfShell,
  buyRareOriginOfShell,
  buyPrimeOriginOfShell,
} from '@/features/pwNftSale/chain'
import {
  createNftSaleMachineConfig,
  createNftSaleMachineOptions,
} from '../nftSaleMachine'
import {
  currentAccountAtom,
  signerAtom,
  useRestoreLastSelectedAccount,
} from '@/features/identity/atoms'
import {
  useAutoConnect,
  websocketConnectionMachineAtom,
} from '@/features/parachain/atoms'
import { querySpiritAttributes } from '@/features/spirit/queries'

import { queryOwnerHasPreorder, querySaleContext } from '../queries'
import { SalePhases } from '../constants'
import { useRouter } from 'next/router'
import { useRefCode } from '@/features/referrals/useRefCode'
import { reportReferrer, getPurchaseCode } from '../requests'

async function buyNFT(context: NftSaleContext) {
  try {
    const {
      api,
      account,
      options: { rarity, career, race },
      canPreorderOriginOfShells,
      canPurchasePrimeOriginOfShells,
      canPurchaseRareOriginOfShells,
      lastDayOfSale,
      signer,
      pwApiEndpoint,
      refCode,
    } = context
    if (!api || !account) {
      return
    }
    console.log('execute buy nft', pwApiEndpoint, refCode)
    if (lastDayOfSale) {
      if (rarity !== LevelEnum.Common) {
        await buyRareOriginOfShell(api, signer!, account, race, career, rarity)
      } else {
        await buyPrimeOriginOfShell(api, signer!, account, race, career)
      }
    } else if (
      (rarity === LevelEnum.Legendary || rarity === LevelEnum.Magic) &&
      canPurchaseRareOriginOfShells
    ) {
      await buyRareOriginOfShell(api, signer!, account, race, career, rarity)
    } else if (
      rarity === LevelEnum.Common &&
      canPurchasePrimeOriginOfShells &&
      !canPreorderOriginOfShells
    ) {
      const [succeed, purchase_code] = await getPurchaseCode(
        PWAPI_ENDPOINT_PREFIX,
        account
      )
      if (succeed) {
        await buyPrimeOriginOfShell(
          api,
          signer!,
          account,
          race,
          career,
          purchase_code,
        )
      }
    } else if (
      rarity === LevelEnum.Common &&
      canPreorderOriginOfShells &&
      !canPurchasePrimeOriginOfShells
    ) {
      await preOrderOriginOfShell(api, signer!, account, race, career)
    } else {
      throw new Error('Unexpected scenario and process failed.')
    }
    await reportReferrer(pwApiEndpoint, account, refCode)
  } catch (err) {
    console.log(err)
    throw err
  }
}

async function doPreordering(context: NftSaleContext) {
  const {
    api,
    account,
    options: { rarity, career, race },
    canPreorderOriginOfShells,
    canPurchasePrimeOriginOfShells,
    lastDayOfSale,
    signer,
  } = context
  if (!api || !account) {
    return
  }
  if (
    rarity === LevelEnum.Common &&
    (canPreorderOriginOfShells || lastDayOfSale) &&
    !canPurchasePrimeOriginOfShells
  ) {
    await preOrderOriginOfShell(api, signer!, account, race, career)
  } else {
    throw new Error('Unexpected scenario and process failed.')
  }
}

function formatBalance(divBase: Decimal, balance: Balance) {
  return parseInt(new Decimal(balance.toString()).div(divBase).toJSON(), 10)
}

const initialContext: Omit<NftSaleContext, 'options' | 'subqlEndpoint' | 'pwApiEndpoint' | 'queryClient'> = {
  canPreorderOriginOfShells: false,
  canPurchasePrimeOriginOfShells: false,
  canPurchaseRareOriginOfShells: false,
  lastDayOfSale: false,
  pha: new Decimal(0),
  inWhiteList: false,
  hasPreOrdered: false,
  spiritClaimed: false,
  ownNFTs: [],
  salePhase: SalePhases.PREPARE,
  prices: {
    [LevelEnum.Legendary]: NaN,
    [LevelEnum.Magic]: NaN,
    [LevelEnum.Common]: NaN,
  },
}

async function initialize(
  context: Pick<
    NftSaleContext,
    'api' | 'account' | 'subqlEndpoint' | 'pwApiEndpoint' | 'hasPreOrdered' | 'queryClient'
  >,
): Promise<Omit<NftSaleContext, 'options' | 'pha' | 'queryClient'>> {
  const { api, account, queryClient } = context

  if (!api) {
    return {
      ...initialContext,
      subqlEndpoint: context.subqlEndpoint,
      pwApiEndpoint: context.pwApiEndpoint,
    }
  }

  const divBase = new Decimal(10).pow(api.registry.chainDecimals[0] || 0)

  const {
    canPreorderOriginOfShells,
    canPurchaseRareOriginOfShells,
    canPurchasePrimeOriginOfShells,
    lastDayOfSale,
    inventoryLevel,
    ...collectionIds
  } = await queryClient.fetchQuery(querySaleContext(api))
  // ])

  if (!account) {
    return {
      ...initialContext,
      canPreorderOriginOfShells,
      canPurchaseRareOriginOfShells,
      canPurchasePrimeOriginOfShells,
      lastDayOfSale,
      inventoryLevel,
      subqlEndpoint: context.subqlEndpoint,
      pwApiEndpoint: context.pwApiEndpoint,
      prices: {
        [LevelEnum.Legendary]: formatBalance(
          divBase,
          api.consts.pwNftSale.legendaryOriginOfShellPrice,
        ),
        [LevelEnum.Magic]: formatBalance(
          divBase,
          api.consts.pwNftSale.magicOriginOfShellPrice,
        ),
        [LevelEnum.Common]: formatBalance(
          divBase,
          api.consts.pwNftSale.primeOriginOfShellPrice,
        ),
      },
    }
  }

  const [
    spiritQuery,
    attrResult,
    ownNFTs,
    ownerHasPreorder,
    currentAccount,
  ] = await Promise.all([
    api.query.uniques.account.entries(
      account,
      collectionIds.spiritCollectionId,
    ),
    queryClient.fetchQuery(querySpiritAttributes(PWAPI_ENDPOINT_PREFIX, account)),
    getOriginOfShellCollectionForAccountAndId(
      api,
      account,
      collectionIds.originOfShellCollectionId,
    ),
    api.query.pwNftSale.ownerHasPreorder(account),
    api.query.system.account(account),
  ])

  return {
    canPreorderOriginOfShells,
    canPurchaseRareOriginOfShells,
    canPurchasePrimeOriginOfShells,
    lastDayOfSale,
    inventoryLevel,
    subqlEndpoint: context.subqlEndpoint,
    pwApiEndpoint: context.pwApiEndpoint,
    inWhiteList: !!R.path(['extra', 'is_whitelisted'], attrResult),
    hasPreOrdered: ownerHasPreorder.toJSON(),
    spiritClaimed: spiritQuery.length > 0,
    spirit: attrResult.owner_address ? attrResult : undefined,
    salePhase: SalePhases.PREPARE,
    ownNFTs,
    prices: {
      [LevelEnum.Legendary]: formatBalance(
        divBase,
        api.consts.pwNftSale.legendaryOriginOfShellPrice,
      ),
      [LevelEnum.Magic]: formatBalance(
        divBase,
        api.consts.pwNftSale.magicOriginOfShellPrice,
      ),
      [LevelEnum.Common]: formatBalance(
        divBase,
        api.consts.pwNftSale.primeOriginOfShellPrice,
      ),
    },
  }
}

export const nftSaleMachineAtom = atomWithMachine<
  INftSaleMachine,
  AnyEventObject
>(
  function (get) {
    const account = get(currentAccountAtom)
    const queryClient = get(queryClientAtom)
    const initialContext: NftSaleContext = {
      subqlEndpoint: get(subqlEndpointAtom),
      pwApiEndpoint: get(pwapiEndpointPrefixAtom),
      queryClient,
      //
      account: account?.address || '',
      hasPreOrdered: false,
      //
      canPreorderOriginOfShells: false,
      canPurchasePrimeOriginOfShells: false,
      canPurchaseRareOriginOfShells: false,
      lastDayOfSale: false,
      pha: new Decimal(0),
      spiritClaimed: false,
      salePhase: SalePhases.PREPARE,
      ownNFTs: [],
      prices: {
        [LevelEnum.Legendary]: NaN,
        [LevelEnum.Magic]: NaN,
        [LevelEnum.Common]: NaN,
      },
      options: {
        rarity: LevelEnum.Common,
        race: RaceEnum.AISpectre,
        career: CareerEnum.HackerWizard,
      },
    }
    return createMachine(
      createNftSaleMachineConfig(initialContext),
      createNftSaleMachineOptions({
        services: {
          doInitializing: initialize,
          doBuying: buyNFT,
          doPreordering: doPreordering,
        },
      }),
    )
  },
  {
    devTools: process.env.NODE_ENV === 'development',
  },
)

nftSaleMachineAtom.debugLabel = 'nftSaleMachine'

function useTrackDepends() {
  const send = useSetAtom(nftSaleMachineAtom)
  const machine = useAtomValue(websocketConnectionMachineAtom)
  const account = useAtomValue(currentAccountAtom)
  const subqlEndpoint = useAtomValue(subqlEndpointAtom)
  const pwApiEndpoint = useAtomValue(pwapiEndpointPrefixAtom)
  const signer = useAtomValue(signerAtom)
  const queryClient = useAtomValue(queryClientAtom)
  const router = useRouter()
  const refCode = useRefCode()

  useEffect(() => {
    send({
      type: 'updateDependencies',
      data: {
        api: machine.context.connection,
        account: account?.address,
        signer,
        refCode,
      },
    })

    ;(async () => {
      let hasPreOrdered = false
      if (machine.context.connection && account?.address) {
        hasPreOrdered = await queryClient.fetchQuery(
          queryOwnerHasPreorder(machine.context.connection, account.address),
        )
      }
      const context = await initialize({
        api: machine.context.connection,
        account: account?.address,
        subqlEndpoint,
        pwApiEndpoint,
        hasPreOrdered,
        queryClient,
      })
      send({ type: 'updateInternalState', data: context })
    })()

    const api = machine.context.connection
    if (api && machine.value === 'connected' && account) {
      let unsubscribe: Function | null = null
      api.query.system
        .account(account.address, balance => {
          const {
            data: { free: freeBalance },
          } = balance
          const multiplier = new Decimal(10).pow(api.registry.chainDecimals[0])
          const value = new Decimal(freeBalance.toString() || '0').div(
            multiplier,
          )
          send({
            type: 'updateBalance',
            data: { pha: value },
          })
        })
        .then(_unsubscribe => {
          unsubscribe = _unsubscribe
        })
      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
      }
    }
  }, [send, machine, account, subqlEndpoint, pwApiEndpoint, signer, queryClient, refCode])

  const { level, species } = router.query
  useEffect(() => {
    const data: Record<string, unknown> = {}
    let touched = false
    if (level && ((level as string) in LEVEL_COLOR_MAP)) {
      data['rarity'] = level as string
      touched = true
    }
    if (species && ((species as string) in RACE_CHAIN_MAP)) {
      data['race'] = species as string
      touched = true
    }
    if (touched) {
      setTimeout(() => {
        send({ type: 'updateOptions', data })
      }, 1)
    }
  }, [send, level, species])
}

export function useStartNftSaleMachine() {
  useRestoreLastSelectedAccount()
  useAutoConnect()
  useTrackDepends()
}
